import BaseLoader from 'components/atoms/base-loader';
import BaseButton from 'components/atoms/baseButton';
import DraggableModal from 'components/organisms/draggableModal';
import { usePatientActivity } from 'infrastructure/hooks/rpm/activity/use-patient-activity';
import { useTranslation } from 'react-i18next';
import { ActivityStatus } from 'infrastructure/enums';
import BaseTextArea from 'components/atoms/base-textarea';
import { useState, type FC } from 'react';
import { useBatchClearing } from 'infrastructure/hooks/rpm/activity/use-batch-clearing';

import s from './styles.module.scss';
import ReadingsList from './reading-list';
import ReadingTypeSelectBox from '../reading-type-select-box';

import type { ReadingType } from 'infrastructure/enums';
import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface BatchClearingModalProps {
  open: boolean;
  patientGuid: string;
  onReadingsRefetch?: () => void;
  onClose: () => void;
}

const BatchClearingModal: FC<BatchClearingModalProps> = ({
  open,
  onClose,
  patientGuid,
  onReadingsRefetch,
}) => {
  const [selectedReadings, setSelectedReadings] = useState<IActivityData[]>([]);
  const [readingType, setReadingType] = useState<ReadingType>();
  const [message, setMessage] = useState();
  const { activity, isLoading } = usePatientActivity({
    patientGuid,
    type: ActivityStatus.Unactioned,
    pageSize: 1000,
    onReadingsRefetch,
    withLinkedCalls: false,
  });
  const { t } = useTranslation();
  const { isLoading: batchClearingLoading, batchClearing } = useBatchClearing();

  const filteredData = activity?.data.filter((r) =>
    readingType ? r.readingType === readingType : true,
  );

  const isAllSelected = selectedReadings.length === activity?.count;

  const onSave = async () => {
    batchClearing(
      {
        message,
        readings: selectedReadings.map((r) => ({
          readingId: r.id,
          deviceId: r.deviceId,
        })),
        patientGuid,
      },
      {
        onSuccess: () => {
          onReadingsRefetch?.();
          setSelectedReadings([]);
          setMessage(undefined);
          onClose();
        },
      },
    );
  };

  const rightTitle = (
    <div className={s['modal-right-title']}>
      <div className={s['reading-type']}>
        <div>{t('labels.readingType')}</div>
        <div style={{ width: '65%' }}>
          <ReadingTypeSelectBox
            value={readingType}
            onChange={(v) => setReadingType(v)}
            allowClear
            withAll
            minWidth="100%"
          />
        </div>
      </div>
      <BaseButton
        width={130}
        height={32}
        label={
          isAllSelected ? t('controls.deselectAll') : t('controls.selectAll')
        }
        type="secondary"
        onClick={() =>
          setSelectedReadings(isAllSelected ? [] : activity?.data ?? [])
        }
      />
    </div>
  );

  const modalFooter = (
    <div className={s.footer}>
      <BaseButton
        label={t('controls.save')}
        onClick={onSave}
        width={130}
        loading={batchClearingLoading}
        disabled={selectedReadings.length === 0}
      />
    </div>
  );

  return (
    <>
      <DraggableModal
        title={`Batch Clearing (${selectedReadings.length})`}
        rightTitle={rightTitle}
        isOpen={open}
        toggle={onClose}
        modalClassName={s.modal}
        bodyClassName={s['modal-body']}
        footer={modalFooter}
      >
        {isLoading ? (
          <BaseLoader loading className={s.loader} />
        ) : (
          <div className={s.wrapper}>
            <div className={s.readings}>
              <ReadingsList
                readings={filteredData}
                selectedReadings={selectedReadings}
                setSelectedReadings={setSelectedReadings}
              />
            </div>
            <div className={s.comment}>
              <BaseTextArea
                style={{ height: '100%' }}
                className={s.textarea}
                placeholder={t('controls.typeYourComment')}
                value={message}
                onChange={setMessage}
              />
            </div>
          </div>
        )}
      </DraggableModal>
    </>
  );
};
export default BatchClearingModal;
